import { ModuleWrapper } from '@/components'
import { Button } from '@/components_deprecated'
import { Input } from '@/components_deprecated/Input'
import { SortableHeader, Table } from '@/components_deprecated/Table'
import type { ExportFormat } from '@/modules/exports'
import { useNodes, useOnErrorAuto, useParam, useUser } from '@/utils/hooks'
import { overloadColor } from '@/utils/style'
import { useMutation, useQuery } from '@apollo/client'
import { useDebounce, useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Navigate } from 'react-router'
import styled from 'styled-components'
import { EXPORT_SALARY_BASE } from './mutations'
import { SALARY_BASE, SALARY_BASE_ENTRIES } from './queries'
import { usePrompt } from '@ur-solutions/react-components'
import { ExportFormatPrompt } from '../companies/ExportFormatPrompt'

const StyledTable = styled(Table)`
  table-layout: fixed;
  td {
    width: 20%;
  }
`

const StyledRow = styled.tr<{ background?: string }>`
  background-color: ${props =>
    props.background ? overloadColor(props.background) : 'auto'};
`

const TableFilter = styled.div`
  display: flex;
  margin-bottom: 1rem;
  padding: 1rem 0;
  background-color: ${props => props.theme.colors.white};
  border-radius: 16px;

  ${props => props.theme.layout.mobile} {
    & > div {
      width: 100%;
      padding: 1rem;
    }
    flex-direction: column;
  }
`

const FilterBlock = styled.div`
  flex-basis: 0;
  flex: 1 1 0px;
  padding: 0 1rem;
  & > label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 0 6px 6px;
    width: 100%;
    color: ${props => props.theme.colors.gray5};
  }
  ${props => props.theme.layout.mobile} {
    display: flex;
    flex-direction: column;
  }
`

export const SalaryBase: React.JSX.CFC = () => {
  const translations = useTranslate({
    name: 'common.name',
    created: 'common.created',
    edit: 'common.edit',
    delete: 'common.delete',
    user: 'common.user',

    filter: 'common.filter',

    userFilter: 'common.user-filter',
    departmentFilter: 'common.department-filter',
    facility: 'common.facility',
    salaryCodeFilter: 'common.salary-code-filter',

    salaryBase: 'common.salary-base',
    salaryBaseLines: 'salary.salary-base-lines',
    externalId: 'common.external-id',
    salaryCode: 'common.salary-code',
    amount: 'common.amount',
    initialAmount: 'salary.initial-amount',
    department: 'common.department',
    export: 'common.export',
  })

  const salaryBaseId = useParam('salaryBaseId')
  const me = useUser()

  const { formValues, formChangeHandler: handler } = useForm({
    values: {
      qUser: '',
      qDepartment: '',
      qFacility: '',
      qSalaryCode: '',
      orderBy: 'user__lastName',
    },
  })

  const debouncedUser = useDebounce(formValues.qUser, 300)
  const debouncedDepartment = useDebounce(formValues.qDepartment, 300)
  const debouncedSalaryCode = useDebounce(formValues.qSalaryCode, 300)

  const onErrorAuto = useOnErrorAuto()
  const addPrompt = usePrompt()

  const { data, loading } = useQuery(SALARY_BASE, {
    variables: {
      id: salaryBaseId,
    },
    fetchPolicy: 'cache-and-network',
  })

  const { data: entryData, loading: entryLoading } = useQuery(
    SALARY_BASE_ENTRIES,
    {
      variables: {
        id: salaryBaseId,
        qUser: debouncedUser,
        qFacility: formValues.qFacility,
        qDepartment: debouncedDepartment,
        qSalaryCode: debouncedSalaryCode,
        orderBy: formValues.orderBy,
      },
      fetchPolicy: 'cache-and-network',
    }
  )

  const [exportSalaryBase, { loading: exportLoading }] = useMutation(
    EXPORT_SALARY_BASE,
    {
      onError: onErrorAuto(),
    }
  )

  const salaryBase = data?.salaryBase
  const entries = useNodes(entryData?.allSalaryBaseEntries)

  async function handleExport() {
    const { data } = await addPrompt<{ exportFormat: ExportFormat } | null>(
      resolve => <ExportFormatPrompt onSubmit={resolve} />
    )

    if (!data) return

    void exportSalaryBase({
      variables: {
        salaryBase: salaryBaseId,
        user: me.id,
        exportFormat: data.exportFormat,
      },
    })
  }

  if (!loading && !salaryBase) return <Navigate to="/salary-bases" />

  return (
    <ModuleWrapper
      title={salaryBase?.name}
      loading={loading}
      headerRight={
        !entryLoading && (
          <Button onClick={handleExport} disabled={exportLoading}>
            {translations.export}
          </Button>
        )
      }
    >
      <TableFilter>
        <FilterBlock>
          <label>{translations.userFilter}</label>
          <Input
            type="text"
            value={formValues.qUser}
            onChange={handler('qUser')}
            grayed
            fullWidth
          />
        </FilterBlock>

        <FilterBlock>
          <label>{translations.departmentFilter}</label>
          <Input
            type="text"
            value={formValues.qDepartment}
            onChange={handler('qDepartment')}
            grayed
            fullWidth
          />
        </FilterBlock>

        <FilterBlock>
          <label>{translations.facility}</label>
          <Input
            type="text"
            value={formValues.qFacility}
            onChange={handler('qFacility')}
            grayed
            fullWidth
          />
        </FilterBlock>

        <FilterBlock>
          <label>{translations.salaryCodeFilter}</label>
          <Input
            type="text"
            value={formValues.qSalaryCode}
            onChange={handler('qSalaryCode')}
            grayed
            fullWidth
          />
        </FilterBlock>

        <FilterBlock></FilterBlock>
      </TableFilter>

      <StyledTable noBorders rowHoverBackground="gray10">
        <thead>
          <tr>
            <SortableHeader
              baseValue="user__lastName"
              sortValue={formValues.orderBy}
              invertDirection
              onSort={handler('orderBy')}
            >
              {translations.user}
            </SortableHeader>

            <SortableHeader
              baseValue="departmentId"
              sortValue={formValues.orderBy}
              invertDirection
              onSort={handler('orderBy')}
            >
              {translations.department}
            </SortableHeader>

            <th>{translations.facility}</th>

            <SortableHeader
              baseValue="facilityId"
              sortValue={formValues.orderBy}
              invertDirection
              onSort={handler('orderBy')}
            >
              {translations.externalId}
            </SortableHeader>

            <SortableHeader
              baseValue="amount"
              sortValue={formValues.orderBy}
              invertDirection
              onSort={handler('orderBy')}
            >
              {translations.amount}
            </SortableHeader>
          </tr>
        </thead>

        <tbody>
          {entries.map(entry => {
            const userText = entry.user.internalNumber
              ? `(${entry.user.internalNumber}) ${entry.user.fullName}`
              : entry.user.fullName

            return (
              <StyledRow
                key={entry.id}
                background={entry.departmentId === '' ? 'red12' : undefined}
              >
                <td>{userText}</td>

                <td>
                  <Input
                    type="text"
                    value={entry.departmentId}
                    onChange={() => {}}
                    disabled
                    grayed
                    fullWidth
                  />
                </td>

                <td>
                  <Input
                    type="text"
                    value={entry.facilityId}
                    onChange={() => {}}
                    disabled
                    grayed
                    fullWidth
                  />
                </td>

                <td>
                  <Input
                    type="text"
                    value={entry.salaryId}
                    onChange={() => {}}
                    disabled
                    grayed
                    fullWidth
                  />
                </td>

                <td>
                  <Input
                    type="text"
                    value={entry.amount.toString()}
                    onChange={() => {}}
                    disabled
                    grayed
                    fullWidth
                  />
                </td>
              </StyledRow>
            )
          })}
        </tbody>
      </StyledTable>
    </ModuleWrapper>
  )
}
